import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./Profile.css";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import CoffeeInviteModal from "../components/CoffeeInviteModal";

// Display labels indexed by JS getDay() (0=Sun … 6=Sat)
const DAY_DISPLAY = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
// TypicalAvailabilityPicker keys indexed by JS getDay()
const JS_TO_SLOT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// before 12 = Morning, 12–15 = Afternoon, 16–18 = Evening
function hourToGroup(h) {
  if (h < 12) return "Morning";
  if (h < 16) return "Afternoon";
  return "Evening";
}

const TIME_GROUP_ORDER = ["Morning", "Afternoon", "Evening"];

function getUpcomingDays(count = 14) {
  const result = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    result.push(d);
  }
  return result;
}

const API_BASE = "https://api-2026.fikacreatives.co";

function joinNonEmpty(arr, sep = ", ") {
  return arr.filter(Boolean).join(sep);
}

function safeUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    return u.toString();
  } catch {
    try {
      const u = new URL(`https://${url}`);
      return u.toString();
    } catch {
      return "";
    }
  }
}

function IconLink({ href, label, children }) {
  if (!href) return null;

  return (
    <a
      className="pLinkIcon"
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      title={label}
    >
      {children}
    </a>
  );
}

export default function Profile() {
  const { personId } = useParams();
  const bootstrap = useAuthBootstrap();

  const [remoteData, setRemoteData] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSharePhone, setInviteSharePhone] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  useEffect(() => {
    if (!personId) return;
    setRemoteLoading(true);
    setRemoteError(null);
    fetch(`${API_BASE}/api/people/${personId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setRemoteData(data);
        } else {
          setRemoteError(data.error || "Failed to load profile");
        }
      })
      .catch(() => setRemoteError("Failed to load profile"))
      .finally(() => setRemoteLoading(false));
  }, [personId]);

  const loading = personId ? remoteLoading : bootstrap.loading;
  const error = personId ? remoteError : bootstrap.error;
  const person = personId ? remoteData?.person ?? null : bootstrap.person;
  const profileAssets = personId ? remoteData?.profileAssets ?? null : bootstrap.profileAssets;


  const fullName = useMemo(() => {
    if (!person) return "";
    return joinNonEmpty([person.first_name, person.last_name], " ");
  }, [person]);

  const specialties = useMemo(() => {
    if (!person) return [];
    return [
      person.creative_specialty1,
      person.creative_specialty2,
      person.creative_specialty3,
    ].filter(Boolean);
  }, [person]);

  console.log ("person is", person);
  const portfolioHref = useMemo(() => safeUrl(person?.portfolio), [person]);
  const favoriteCafe = profileAssets?.favorite_cafe || person?.favorite_cafe || "";

  const sender = bootstrap.person;

  async function handleInvite(e) {
    e.preventDefault();
    setInviteSending(true);
    setInviteError("");
    try {
      const res = await fetch(`${API_BASE}/api/coffee-invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          inviteeId: personId,
          senderPersonId: sender?.id,
          senderFirstName: sender?.first_name || "",
          senderLastName: sender?.last_name || "",
          message: inviteMessage.trim(),
          sharePhone: inviteSharePhone,
          suggestedDate: selectedDay ? selectedDay.toISOString().split("T")[0] : null,
          suggestedTime: selectedTime || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send invite");
      setInviteSent(true);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteSending(false);
    }
  }

  function closeInviteModal() {
    setInviteOpen(false);
    setInviteMessage("");
    setInviteSharePhone(false);
    setInviteError("");
    setInviteSent(false);
  }
  const profileImageUrl = profileAssets?.profile_image_url || "";
  const projectImageUrl = profileAssets?.project_image_url || "";
  const favoriteDrink = profileAssets?.favorite_drink || person?.favorite_drink || "";
  const primaryProjectDescription = profileAssets?.primary_project_description || "";
  const favoriteBook   = profileAssets?.favorite_book   || "";
  const favoriteMovie  = profileAssets?.favorite_movie  || "";
  const favoriteArtist = profileAssets?.favorite_artist || "";
  const nerdOutOn      = profileAssets?.nerd_out_on     || "";


  // typical_availability: array of slot strings like "Mon 09:00", "Tue 14:00"
  const availabilitySlots = useMemo(() => {
    const raw = profileAssets?.typical_availability ?? person?.typical_availability;
    return Array.isArray(raw) ? raw : [];
  }, [profileAssets, person]);

  // Unique time-group labels for a given weekday abbreviation
  const inviteDays = useMemo(() => {
    const all = getUpcomingDays(14);
    if (!availabilitySlots.length) return all.slice(0, 7);
    return all
      .filter(d => {
        const abbrev = JS_TO_SLOT_DAY[d.getDay()];
        const hours = availabilitySlots
          .filter(s => s.startsWith(abbrev + " "))
          .map(s => parseInt(s.split(":")[0].split(" ")[1]));
        return hours.length > 0;
      })
      .slice(0, 7);
  }, [availabilitySlots]);

  const availableTimesForDay = useMemo(() => {
    if (!selectedDay || !availabilitySlots.length) return [];
    const abbrev = JS_TO_SLOT_DAY[selectedDay.getDay()];
    const hours = availabilitySlots
      .filter(s => s.startsWith(abbrev + " "))
      .map(s => parseInt(s.split(":")[0].split(" ")[1]));
    const seen = new Set(hours.map(hourToGroup));
    return TIME_GROUP_ORDER.filter(g => seen.has(g));
  }, [selectedDay, availabilitySlots]);

  if (loading) {
    return (
      <div className="pPage">
        <div className="pModal">
          <div className="pInner">
            <p className="pMuted">Loading profile…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pPage">
        <div className="pModal">
          <div className="pInner">
            <p className="pErr">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="pPage">
        <div className="pModal">
          <div className="pInner">
            <p className="pErr">Not authenticated.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="pPage">
      <div className="full">
        <div className="pModal" role="dialog" aria-modal="true">
          <div className="pInner">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 12,
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 8,
              }}
            >

        
            </div>

            {/* Desktop layout: image left, text right */}
            <div className="pTop pTop--desktop">
              <div className="pAvatarWrap">
                {profileImageUrl ? (
                  <img className="pAvatar" src={profileImageUrl} alt={`${fullName || "User"} profile`} />
                ) : (
                  <div className="pAvatar pAvatarFallback">{person.first_name?.slice(0, 1) || "?"}</div>
                )}
              </div>
              <div className="pHead">
                <h1 className="pName">{fullName || "Your Profile"}</h1>
                {specialties.length > 0 && <p className="pSubtitle">{specialties.join(",  ")}</p>}
                {person?.neighborhood && (
                  <p className="pSubtitle" style={{ color: "rgba(0,0,0,0.45)" }}>Based in {person.neighborhood}</p>
                )}
                {person?.bio ? <p className="pCardBody">{person.bio}</p> : null}
              </div>
            </div>

            {/* Mobile layout: image → name → specialty → location → bio stacked */}
            <div className="pMobile">
              {profileImageUrl ? (
                <img className="pMobileImg" src={profileImageUrl} alt={`${fullName || "User"} profile`} />
              ) : (
                <div className="pMobileImg pAvatarFallback">{person.first_name?.slice(0, 1) || "?"}</div>
              )}
              <div className="pMobileInfo">
                <h1 className="pName">{fullName || "Your Profile"}</h1>
                {specialties.length > 0 && <p className="pSubtitle">{specialties.join(",  ")}</p>}
                {person?.neighborhood && (
                  <p className="pSubtitle" style={{ color: "rgba(0,0,0,0.45)" }}>Based in {person.neighborhood}</p>
                )}
                {person?.bio ? <p className="pCardBody">{person.bio}</p> : null}
              </div>
            </div>

            <div className="pMetaRow">
              <div className="pMetaBlock">
                <div className="pMetaLabel">Links</div>
                <div className="pLinks">
                  <IconLink href={portfolioHref} label="Portfolio">
                    <span className="pLinkText">Portfolio</span>
                  </IconLink>
                </div>
              </div>

              <div className="pMetaBlock">
                <div className="pMetaLabel">Currently</div>
                {person.company ? <span>{person.company}</span> : <span> {person.current_role_and_company}</span>}
              </div>
            </div>

            {primaryProjectDescription ? (
              <>
                <h2 className="pSectionTitle">Project in Mind</h2>
                <p className="pCardBody">{primaryProjectDescription}</p>
              </>
            ) : null}

            <h2 className="pSectionTitle">Work</h2>

            {projectImageUrl ? (
              <img
                className="pWorkImg"
                src={projectImageUrl}
                alt="Featured project"
                loading="lazy"
              />
            ) : (
              <div className="pWorkEmpty">
                <div className="pMuted">
                  Favorite work hasn't been uploaded yet.{portfolioHref ? <> Check their <a href={portfolioHref} target="_blank" rel="noreferrer" style={{ color: "#2b62ff" }}>portfolio</a> to view work!</> : " Check their portfolio to view work!"}
                </div>
              </div>
            )}

            {(favoriteBook || favoriteMovie || favoriteArtist || nerdOutOn) && (
              <>
                <h2 className="pSectionTitle">Fun Facts</h2>
                <div className="pFunFactsGrid">
                  {favoriteBook && (
                    <div className="pFunFactCard pFunFactCard--blue">
                      <span className="pFunFactLabel">Favorite book</span>
                      <span className="pFunFactValue">{favoriteBook}</span>
                    </div>
                  )}
                  {favoriteMovie && (
                    <div className="pFunFactCard pFunFactCard--taupe">
                      <span className="pFunFactLabel">Movie I'd watch on repeat</span>
                      <span className="pFunFactValue">{favoriteMovie}</span>
                    </div>
                  )}
                  {favoriteArtist && (
                    <div className="pFunFactCard pFunFactCard--sage">
                      <span className="pFunFactLabel">An artist I love</span>
                      <span className="pFunFactValue">{favoriteArtist}</span>
                    </div>
                  )}
                  {nerdOutOn && (
                    <div className="pFunFactCard pFunFactCard--rose">
                      <span className="pFunFactLabel">I nerd out on</span>
                      <span className="pFunFactValue">{nerdOutOn}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="inviteCard">
          <p className="inviteTitle">
            Meet <strong>{person.first_name}</strong> at{" "}
            <span className="invitePlace">{favoriteCafe || "their favorite spot"}</span>{" "}
            for a {favoriteDrink || "coffee"}.
          </p>

          {availabilitySlots.length > 0 && (
            <>
              <div className="inviteDaysRow">
                {inviteDays.map((day, i) => {
                  const isActive = selectedDay && day.toDateString() === selectedDay.toDateString();
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`inviteDayPill${isActive ? " inviteDayPill--active" : ""}`}
                      onClick={() => { setSelectedDay(day); setSelectedTime(null); }}
                    >
                      <span className="inviteDayName">{DAY_DISPLAY[day.getDay()]}</span>
                      <span className="inviteDayNum">{day.getDate()}</span>
                    </button>
                  );
                })}
              </div>

              {selectedDay && (
                <div className="inviteTimesRow">
                  {availableTimesForDay.map(t => (
                    <button
                      key={t}
                      type="button"
                      className={`inviteTimePill${selectedTime === t ? " inviteTimePill--active" : ""}`}
                      onClick={() => setSelectedTime(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <button
            type="button"
            className="inviteSendBtn"
            onClick={() => setInviteOpen(true)}
          >
             Invite to coffee
          </button>

          <p className="inviteNote">{person.first_name} will have 24 hours to respond.</p>
        </div>
      </div>
    </div>

    <div className="pStickyInvite">
      <button
        type="button"
        className="pStickyInviteBtn"
        onClick={() => setInviteOpen(true)}
      >
        Invite to coffee
      </button>
    </div>

    {inviteOpen && (
      <CoffeeInviteModal
        inviteeName={person.first_name}
        inviteeFullName={fullName}
        days={availabilitySlots.length > 0 ? inviteDays : undefined}
        availableTimesForDay={availableTimesForDay}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
        selectedTime={selectedTime}
        onSelectTime={setSelectedTime}
        message={inviteMessage}
        onMessageChange={setInviteMessage}
        sharePhone={inviteSharePhone}
        onSharePhoneChange={setInviteSharePhone}
        sending={inviteSending}
        sent={inviteSent}
        error={inviteError}
        onSubmit={handleInvite}
        onClose={closeInviteModal}
      />
    )}
    </>
  );
}